import {
  bg,
  cs,
  da,
  de,
  enCA,
  enGB,
  enUS,
  es,
  et,
  fi,
  fr,
  frCA,
  hu,
  it,
  lt,
  lv,
  nb,
  nl,
  pl,
  pt,
  ptBR,
  ro,
  ru,
  sk,
  sv,
  uk,
} from 'date-fns/locale';
import { PickerMode } from '../components/molecules/date-picker-field/_core/control-value';

/**
 * Single place where a locale becomes a date/time pattern.
 *
 * The patterns themselves are not written down here — they are derived from the CLDR data
 * the engine already ships, through `Intl`. Adding a country therefore costs nothing for
 * the mask; only the Material calendar needs a row, and not because `Intl` lacks the month
 * and weekday names — it has them — but because `DateFnsAdapter.setLocale` accepts a
 * date-fns `Locale` instance and nothing else.
 *
 * It lives in `ui-kit` because `ui-kit` is tagged `type:ui` and may not depend on any
 * other lib: the picker has to be able to read this, so it has to be here. Everything
 * outside imports it from `@shared-ui-kit`.
 */
export interface DateFormatProfile {
  /** The resolved BCP-47 tag. Also passed straight to `Intl` by the picker. */
  readonly id: string;
  /** Compiled into the mask, e.g. `MM/dd/yyyy`. */
  readonly datePattern: string;
  /** `hh:mm aa` for 12-hour locales, `HH:mm` for 24-hour ones. */
  readonly timePattern: string;
  readonly hour12: boolean;
  readonly rangeSeparator: string;
  /** Characters that advance to the next field regardless of the pattern's own literals. */
  readonly acceptedSeparators: readonly string[];
  /** Month and weekday names for the Material `DateAdapter`. */
  readonly dateFns: typeof enUS;
}

/** The locale a user gets when nothing better is known — see `CoreService.getUserLocale()`. */
export const DEFAULT_USER_LOCALE = 'en-US';

/**
 * The backend stores a language (`EN`/`ES`), not a locale, so this is where the region
 * comes from. `es` alone would mean a 24-hour European Spanish, which is not the market.
 */
export const LOCALE_BY_LANGUAGE: Readonly<Record<string, string>> = {
  en: 'en-US',
  es: 'es-MX',
};

const COMMON_SEPARATORS = ['/', '.', '-', ' ', ',', ':'] as const;
const RANGE_SEPARATOR = ' – ';

const HOUR_24 = 'HH:mm';
const HOUR_12 = 'hh:mm aa';

/**
 * date-fns has no Mexican Spanish; `es` carries the same month and weekday names. A tag
 * with no row falls back to its base language and then to `enUS`, which only costs the
 * calendar its localized names — the mask still follows the tag's own CLDR format.
 */
const DATE_FNS_LOCALES: Readonly<Record<string, typeof enUS>> = {
  'en-US': enUS,
  'en-CA': enCA,
  'en-GB': enGB,
  'fr-CA': frCA,
  'pt-BR': ptBR,
  'es-MX': es,
  bg,
  cs,
  da,
  de,
  es,
  et,
  fi,
  fr,
  hu,
  it,
  lt,
  lv,
  nb,
  nl,
  pl,
  pt,
  ro,
  ru,
  sk,
  sv,
  uk,
};

const dateFnsFor = (tag: string): typeof enUS => {
  const [language = ''] = tag.split('-');
  return DATE_FNS_LOCALES[tag] ?? DATE_FNS_LOCALES[language] ?? enUS;
};

/** Day and month differ so a `formatToParts` run cannot confuse one for the other. */
const REFERENCE_YEAR = 2026;
const NOON = 12;
const REFERENCE = new Date(Date.UTC(REFERENCE_YEAR, 0, 2, NOON));

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  // Thailand would otherwise date in the Buddhist era and Egypt in Arabic-Indic digits,
  // and `2-digit` is what makes CLDR pad the fields the fixed-width mask needs.
  calendar: 'gregory',
  numberingSystem: 'latn',
  timeZone: 'UTC',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const TOKEN_BY_PART: Readonly<Partial<Record<Intl.DateTimeFormatPartTypes, string>>> = {
  day: 'dd',
  month: 'MM',
  year: 'yyyy',
};

/**
 * CLDR wraps RTL separators in bidi marks and spaces some literals with a narrow no-break
 * space. Both are invisible, and both would land in the mask and shift every caret index
 * after them. Written as escapes so no formatter or editor can silently eat them.
 */
const BIDI_MARKS = /[\u200E\u200F\u061C]/g;
const FIXED_WIDTH_SPACES = /[\u202F\u00A0]/g;

/**
 * What a mask literal is allowed to contain: a bidi mark is dropped outright, a fixed-width
 * space becomes an ordinary one so it matches both `acceptedSeparators` and what a user
 * types. Exported because the character class is the fiddly part, and no locale exercises
 * more than two of its five members.
 */
export const readableLiteral = (value: string): string =>
  value.replace(BIDI_MARKS, '').replace(FIXED_WIDTH_SPACES, ' ');

/**
 * The four CLDR hour cycles split by one question: does the hour alone tell morning from
 * evening? h11 (0–11) and h12 (1–12) need an AM/PM marker; h23 (0–23) and the exotic h24,
 * where midnight reads 24:30, do not. `resolvedOptions()` answers that for us — and keeps
 * answering it if a fifth cycle is ever added.
 */
const isHour12 = (tag: string): boolean =>
  new Intl.DateTimeFormat(tag, { hour: 'numeric' }).resolvedOptions().hour12 ?? false;

/**
 * `Intl` accepts BCP-47 and nothing else: a date-fns export name such as `enUS` throws
 * rather than falling back, and a well-formed but unknown tag silently lands on the
 * *host's* locale instead of ours. Both have to be turned into our own default here.
 *
 * The tag comes back canonicalized, so `EN-us` and `en-US` share one cached profile.
 */
function supportedTag(locale: string | null | undefined): string {
  if (!locale) return DEFAULT_USER_LOCALE;

  try {
    const [supported] = Intl.DateTimeFormat.supportedLocalesOf(locale);

    return supported ?? DEFAULT_USER_LOCALE;
  } catch {
    return DEFAULT_USER_LOCALE;
  }
}

function buildProfile(tag: string): DateFormatProfile {
  const formatter = new Intl.DateTimeFormat(tag, DATE_OPTIONS);

  const datePattern = formatter
    .formatToParts(REFERENCE)
    .map((part) => TOKEN_BY_PART[part.type] ?? readableLiteral(part.value))
    .join('');

  const hour12 = isHour12(tag);

  return {
    id: formatter.resolvedOptions().locale,
    datePattern,
    timePattern: hour12 ? HOUR_12 : HOUR_24,
    hour12,
    rangeSeparator: RANGE_SEPARATOR,
    acceptedSeparators: [...COMMON_SEPARATORS],
    dateFns: dateFnsFor(tag),
  };
}

/**
 * Cached because the picker reads the profile from a `computed()` on every keystroke, and
 * building one constructs two `Intl.DateTimeFormat` instances.
 */
const cache = new Map<string, DateFormatProfile>();

function profileFor(tag: string): DateFormatProfile {
  const cached = cache.get(tag);
  if (cached) return cached;

  const built = buildProfile(tag);
  cache.set(tag, built);

  return built;
}

/**
 * Anything `Intl` can read resolves to that locale's own CLDR format; everything else
 * resolves to the default. Region tags need no special handling — `de-DE` is a locale
 * CLDR knows, so it is answered directly rather than through its base language.
 */
export function resolveDateFormatProfile(locale: string | null | undefined): DateFormatProfile {
  return profileFor(supportedTag(locale));
}

export const DEFAULT_DATE_FORMAT_PROFILE = profileFor(DEFAULT_USER_LOCALE);

/** Builds the pattern for a mode out of a profile's date and time halves. */
export function patternForMode(
  profile: DateFormatProfile,
  mode: PickerMode,
  hour12Override?: boolean,
): string {
  const timePattern =
    hour12Override === undefined ? profile.timePattern : hour12Override ? HOUR_12 : HOUR_24;

  switch (mode) {
    case PickerMode.Time:
      return timePattern;
    case PickerMode.DateTime:
      return `${profile.datePattern} ${timePattern}`;
    case PickerMode.Date:
    case PickerMode.Range:
      return profile.datePattern;
  }
}
